import React, { useState } from 'react';
import {
  Title,
  Card,
  Table,
  Badge,
  Button,
  Group,
  Text,
  ActionIcon,
  Loader,
  Modal,
  Select,
  Stack,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../../components/ui/PageContainer';
import {
  useQuestionTemplates,
  useDeleteQuestionTemplate,
  useCompetencyMaps,
  useGenerateTemplateFromMap,
} from '../../../hooks/useApi';
import { showToast } from '../../../utils/toast';
import { getErrorMessage } from '../../../utils/api';
import { QuestionTemplate } from '../../../types';

export const Review360Templates: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: templates = [], isLoading } = useQuestionTemplates();
  const deleteTemplate = useDeleteQuestionTemplate();
  const { data: maps = [] } = useCompetencyMaps();
  const generateFromMap = useGenerateTemplateFromMap();

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);

  const handleDelete = (template: QuestionTemplate) => {
    modals.openConfirmModal({
      title: t('review360.deleteTemplate'),
      centered: true,
      children: (
        <Text size="sm">{t('review360.deleteTemplateConfirm', { name: template.name })}</Text>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteTemplate.mutate(template._id, {
          onSuccess: () => {
            showToast({
              title: t('review360.templateDeleted'),
              message: t('review360.templateDeletedMsg'),
              color: 'teal',
            });
          },
          onError: (error) => {
            const message = getErrorMessage(error, t('common.error'));
            showToast({ title: t('common.error'), message, color: 'red' });
          },
        });
      },
    });
  };

  const handleGenerateFromMap = () => {
    if (!selectedMapId) return;
    generateFromMap.mutate(selectedMapId, {
      onSuccess: (newTemplate: QuestionTemplate) => {
        setIsMapModalOpen(false);
        setSelectedMapId(null);
        navigate(`/review-360/templates/${newTemplate._id}`);
      },
      onError: (error) => {
        const message = getErrorMessage(error, t('common.error'));
        showToast({ title: t('common.error'), message, color: 'red' });
      },
    });
  };

  const mapOptions = maps.map((m) => ({
    value: m._id,
    label: m.name,
  }));

  const getQuestionsCount = (template: QuestionTemplate) =>
    template.sections.reduce((sum, s) => sum + s.questions.length, 0);

  return (
    <PageContainer>
      <Group justify="space-between" mb="xl">
        <Group gap="sm">
          <Title order={2}>{t('review360.templates')}</Title>
          <Badge size="lg" variant="light" circle>
            {templates.length}
          </Badge>
        </Group>
        <Group gap="sm">
          <Button variant="light" onClick={() => setIsMapModalOpen(true)}>
            {t('review360.fromMap')}
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/review-360/templates/new')}
          >
            {t('review360.createTemplate')}
          </Button>
        </Group>
      </Group>

      <Card withBorder padding={0}>
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : templates.length === 0 ? (
          <Text ta="center" py="xl" c="dimmed">
            {t('review360.noTemplates')}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('review360.name')}</Table.Th>
                <Table.Th>{t('review360.sections')}</Table.Th>
                <Table.Th>{t('review360.questions')}</Table.Th>
                <Table.Th>{t('review360.default')}</Table.Th>
                <Table.Th>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {templates.map((tmpl) => (
                <Table.Tr key={tmpl._id}>
                  <Table.Td>{tmpl.name}</Table.Td>
                  <Table.Td>{tmpl.sections.length}</Table.Td>
                  <Table.Td>{getQuestionsCount(tmpl)}</Table.Td>
                  <Table.Td>
                    {tmpl.isDefault && (
                      <Badge variant="light" color="teal">
                        {t('review360.default')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="light"
                        onClick={() => navigate(`/review-360/templates/${tmpl._id}`)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" onClick={() => handleDelete(tmpl)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal
        opened={isMapModalOpen}
        onClose={() => {
          setIsMapModalOpen(false);
          setSelectedMapId(null);
        }}
        title={t('review360.fromMap')}
        centered
      >
        <Stack gap="md">
          <Select
            label={t('review360.selectMap')}
            placeholder={t('review360.selectMap')}
            data={mapOptions}
            value={selectedMapId}
            onChange={setSelectedMapId}
            searchable
          />
          <Group justify="flex-end">
            <Button
              onClick={handleGenerateFromMap}
              loading={generateFromMap.isPending}
              disabled={!selectedMapId}
            >
              {t('review360.generate')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
};
